import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { Box, Button, Card, CardContent, Chip, Container, Grid, Stack, Typography } from '@mui/material';
import PageBanner from '../../components/Common/PageBanner';
import SectionHeading from '../../components/Common/SectionHeading';
import ProductImageSlider from '../../components/Products/ProductImageSlider';
import productBannerImage from '../../assets/productBanner.jpeg';
import { pageContent, productRanges } from '../../data/siteData';
import { getProductImages } from '../../utils/productAssets';

function ProductsPage() {
  const content = pageContent.products;
  const [activeFilter, setActiveFilter] = useState(content.filterLabels[0]);
  const navigate = useNavigate();

  const filteredRanges = useMemo(() => {
    if (activeFilter === 'All') {
      return productRanges;
    }
    return productRanges.filter((range) => range.filterLabel === activeFilter);
  }, [activeFilter]);

  const rangeImages = useMemo(
    () =>
      productRanges.reduce((imagesByRange, range) => {
        imagesByRange[range.id] = getProductImages(range.assetFolderName);
        return imagesByRange;
      }, {}),
    []
  );

  const openCategory = (slug) => {
    navigate(`/products/${slug}`);
  };

  return (
    <Box>
      <PageBanner {...content.banner} />
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        <Card
          elevation={0}
          sx={{
            mb: { xs: 5, md: 6 },
            overflow: 'hidden',
            borderRadius: 4,
            border: '1px solid rgba(74,14,143,0.12)',
            background: 'linear-gradient(180deg, #ffffff 0%, #fffdfa 100%)',
            boxShadow: '0 24px 60px rgba(16, 8, 30, 0.08)',
          }}
        >
          <Box
            sx={{
              px: { xs: 2.5, md: 3.5 },
              py: { xs: 2, md: 2.5 },
              borderBottom: '1px solid rgba(74,14,143,0.08)',
              background:
                'linear-gradient(135deg, rgba(74,14,143,0.05) 0%, rgba(201,168,76,0.1) 100%)',
            }}
          >
            <Typography
              variant="overline"
              sx={{ color: '#4a0e8f', letterSpacing: '0.16em', fontWeight: 700 }}
            >
              Complete Product Overview
            </Typography>
            <Typography variant="h5" sx={{ color: '#1f1332' }}>
              Product list banner
            </Typography>
          </Box>
          <Box
            sx={{
              p: 0,
              bgcolor: '#ffffff',
            }}
          >
            <Box
              component="img"
              src={productBannerImage}
              alt="SCIO product banner showing all product names"
              sx={{
                display: 'block',
                width: '100%',
                height: 'auto',
                borderRadius: 0,
              }}
            />
          </Box>
        </Card>
        <SectionHeading
          eyebrow={content.heading.eyebrow}
          title={content.heading.title}
          subtitle={content.heading.subtitle}
        />
        <Stack direction="row" flexWrap="wrap" gap={1.2} justifyContent="center" sx={{ mb: 4 }}>
          {content.filterLabels.map((label) => (
            <Chip
              key={label}
              label={label}
              onClick={() => setActiveFilter(label)}
              sx={{
                px: 1,
                py: 2.35,
                cursor: 'pointer',
                bgcolor: activeFilter === label ? '#4a0e8f' : '#fffdfa',
                color: activeFilter === label ? '#ffffff' : '#4a0e8f',
                border: '1px solid rgba(74,14,143,0.14)',
              }}
            />
          ))}
        </Stack>
        <Grid container spacing={3}>
          {filteredRanges.map((range) => {
            const Icon = range.icon;
            return (
              <Grid item xs={12} sm={6} md={4} key={range.id}>
                <Card
                  elevation={0}
                  role="button"
                  tabIndex={0}
                  onClick={() => openCategory(range.slug)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openCategory(range.slug);
                    }
                  }}
                  sx={{ height: '100%', cursor: 'pointer' }}
                >
                  <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <ProductImageSlider images={rangeImages[range.id]} height={210} />
                    <Box
                      sx={{
                        width: 62,
                        height: 62,
                        borderRadius: 3.5,
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: range.accent,
                        color: range.color,
                        my: 2.5,
                      }}
                    >
                      <Icon />
                    </Box>
                    <Typography variant="h4" sx={{ mb: 1.2 }}>{range.label || range.name}</Typography>
                    <Typography sx={{ color: 'text.secondary', lineHeight: 1.75, mb: 2.5 }}>
                      {range.description}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={(event) => {
                        event.stopPropagation();
                        openCategory(range.slug);
                      }}
                      sx={{ mt: 'auto', alignSelf: 'flex-start' }}
                    >
                      {content.enquireButton}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        <Stack alignItems="center" sx={{ mt: 4 }}>
          <Button variant="outlined" color="secondary" startIcon={<DownloadRoundedIcon />}>
            {content.downloadButton}
          </Button>
        </Stack>
      </Container>
    </Box>
  );
}

export default ProductsPage;
